<template>
  <transition name="bottom-sheet-slide-up">
    <div
      v-if="controller.isVisible(props.name)"
      class="fixed inset-0 z-1000 flex"
      :class="{
        'pointer-events-none': withoutBlackout,
      }"
      :data-swipe-back-ignore="Boolean(persistent)"
    >
      <div
        class="bottom-sheet-container__blackout absolute inset-0 z-10"
        :class="[
          withoutBlackout
            ? ''
            : blackoutClass || 'bg-static-black-secondary/80',
          { 'backdrop-blur-xs': withBlur },
        ]"
        data-qa-id="bottomSheet.blackout"
        @touchstart.prevent.stop="onBlackoutTouchStart"
        @touchend="onBlackoutTouchEnd"
      ></div>
      <div class="bottom-sheet absolute bottom-0 z-10 flex w-full flex-col">
        <div
          ref="bottomSheetRef"
          style="transition: min-height 0.4s ease-out"
          :style="
            bottomSheetState.sheetShift > 0
              ? { transform: `translateY(${bottomSheetState.sheetShift}px)` }
              : {}
          "
          class="bottom-sheet__content-wrapper transition-all duration-300"
          role="dialog"
          aria-modal="true"
        >
          <div
            v-if="isHandleBarVisible"
            class="flex h-4 w-full shrink-0 items-center justify-center"
            @touchstart.stop="onSheetTouchStart"
            @touchmove.stop="onSheetTouchMove"
            @touchend.stop="onSheetTouchEnd"
            @click="onHandleBarClick"
          >
            <span class="bg-static-grey h-1 w-14 rounded-full"></span>
          </div>
          <div
            class="bottom-sheet__content bg-background-neutral-primary flex w-full flex-col overflow-hidden duration-300"
            :class="{
              'bottom-sheet__content--full': fullHeight,
              'bottom-sheet__content--fullscreen': fullScreen,
            }"
            style="
              transition:
                min-height 0.4s ease-out,
                border-radius 0.4s ease-out 0.4s;
            "
            :style="panelStyle"
          >
            <div
              class="shrink-0"
              @touchstart.stop="onSheetTouchStart"
              @touchmove="onSheetTouchMove"
              @touchend="onSheetTouchEnd"
            >
              <slot
                name="header"
                v-bind="{
                  hide,
                  params: controller.getParams(props.name) || {},
                }"
              ></slot>
            </div>
            <div
              v-bind="$attrs"
              :id="name"
              ref="contentRef"
              class="flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-none pb-4"
              :data-preserve-scroll="name"
              @touchstart.stop="onContentTouchStart"
              @touchmove="onSheetTouchMove"
              @touchend="onSheetTouchEnd"
            >
              <slot
                v-bind="{
                  hide,
                  params: controller.getParams(props.name) || {},
                }"
              ></slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
  import { ref, watch, onBeforeUnmount, type StyleValue } from 'vue';
  import { controllerModel } from 'shared';

  const props = withDefaults(
    defineProps<{
      name: string;
      persistent?: boolean;
      isHandleBarVisible?: boolean;
      /**
       * Sheet can be dismissed by a gesture: a swipe down over the header /
       * content, or a tap on the blackout next to the panel. `false` leaves the
       * sheet's own controls (a close button calling the slot's `hide`) as the
       * only way out — meant for sheets a stray gesture must not close, e.g.
       * the full-screen AI chat, whose blackout is exposed as a notch beside
       * the panel it slides aside (SEARCH-2175).
       */
      isClosable?: boolean;
      withoutBlackout?: boolean;
      /**
       * Class for the blackout layer, replacing the default dim. Meant for
       * full-screen sheets that move their panel aside and expose the blackout
       * next to it — e.g. the AI chat slides the panel out to reveal its
       * slide-out menu, and the rounded corners of the moved panel would show
       * the dim as black notches instead of the page surface behind the card.
       * Ignored together with the layer itself when `withoutBlackout` is set.
       */
      blackoutClass?: string;
      withBlur?: boolean;
      // Sheet fills the viewport height minus the top spacing / status bar.
      fullHeight?: boolean;
      // Sheet covers the whole screen: 100dvh, no top gap, no top rounding.
      fullScreen?: boolean;
      /**
       * Inline styles for the sheet panel itself (the box that holds the header
       * and the scrollable content). Meant for callers that move the whole
       * panel — e.g. the AI chat slides it aside to reveal a slide-out menu —
       * because a transform there also carries the panel's `position: fixed`
       * children along. `$attrs` reach only the inner scroll container, which
       * is not enough for that. Overrides the panel's own transition, so pass
       * the transition together with the properties being animated.
       */
      panelStyle?: StyleValue;
    }>(),
    {
      isHandleBarVisible: true,
      isClosable: true,
    },
  );

  const emit = defineEmits<{
    onClose: [];
    onOpen: [];
    onTryClose: [];
  }>();

  defineOptions({ inheritAttrs: false });

  const controller = controllerModel.useBottomSheet();
  const bottomSheetRef = ref<HTMLElement>();
  const contentRef = ref<HTMLDivElement>();
  const bottomSheetState = ref({
    blackoutTouchStarted: false,
    sheetTouchStarted: false,
    sheetTouchStart: 0,
    sheetShift: 0,
  });

  const hide = () => {
    // Inside controller.hide method there is a check for element visibility.
    // But for emit logic there is need to check here.
    if (!props.persistent) {
      controller.hide(props.name);
    } else if (controller.isVisible(props.name)) {
      emit('onTryClose');
    }
  };

  watch(controller.state, (_, prevState) => {
    const wasActive = prevState.find(elem => elem.activeName === props.name);
    if (!controller.isVisible(props.name) && wasActive) {
      emit('onClose');
    }
    if (controller?.isVisible(props.name) && !wasActive) {
      emit('onOpen');
    }
  });

  onBeforeUnmount(() => controller.hide(props.name));

  const onBlackoutTouchStart = () => {
    if (!props.isClosable) {
      return;
    }
    bottomSheetState.value.blackoutTouchStarted = true;
  };

  const onBlackoutTouchEnd = () => {
    if (bottomSheetState.value.blackoutTouchStarted) {
      bottomSheetState.value.blackoutTouchStarted = false;
      hide();
    }
  };

  const onHandleBarClick = () => hide();

  const extractTouch = (e: TouchEvent) => e.changedTouches[0].clientY;

  const isContentScrollAtTop = () => {
    return contentRef.value?.scrollTop === 0;
  };

  const onSheetTouchStart = (e: TouchEvent) => {
    if (!props.isClosable) {
      return;
    }
    bottomSheetState.value.sheetTouchStarted = true;
    bottomSheetState.value.sheetTouchStart = extractTouch(e);
  };

  const onContentTouchStart = (e: TouchEvent) => {
    if (isContentScrollAtTop() && props.isClosable) {
      onSheetTouchStart(e);
    }
  };

  const onSheetTouchMove = (e: TouchEvent) => {
    if (bottomSheetState.value.sheetTouchStarted) {
      const shift = extractTouch(e) - bottomSheetState.value.sheetTouchStart;
      bottomSheetState.value.sheetShift = Math.max(0, shift);

      /**
       * should prevent default behaviour only when bottom sheet starts to shift
       * otherwise other scrollable children elements won't work
       */
      if (shift > 0) {
        e.preventDefault();
      }
    }
  };

  const onSheetTouchEnd = () => {
    if (!bottomSheetRef.value) {
      return;
    }

    const bottomSheetHeight = bottomSheetRef.value.offsetHeight;
    const closingLimit = bottomSheetHeight * 0.1;
    if (
      bottomSheetState.value.sheetTouchStarted &&
      bottomSheetState.value.sheetShift >= closingLimit
    ) {
      hide();
    }

    bottomSheetState.value.sheetTouchStarted = false;
    bottomSheetState.value.sheetShift = 0;
  };

  defineExpose({ contentRef });
</script>

<style scoped>
  .bottom-sheet-slide-up-enter-active,
  .bottom-sheet-slide-up-leave-active {
    transition: 500ms;
  }

  .bottom-sheet-slide-up-enter-active > .bottom-sheet-container__blackout,
  .bottom-sheet-slide-up-leave-active > .bottom-sheet-container__blackout {
    transition: 500ms;
  }

  .bottom-sheet-slide-up-enter-from > .bottom-sheet-container__blackout,
  .bottom-sheet-slide-up-leave-to > .bottom-sheet-container__blackout {
    opacity: 0;
  }

  .bottom-sheet-slide-up-enter-active > .bottom-sheet,
  .bottom-sheet-slide-up-leave-active > .bottom-sheet {
    transition: 200ms ease-in;
  }
  .bottom-sheet-slide-up-enter-from > .bottom-sheet,
  .bottom-sheet-slide-up-leave-to > .bottom-sheet {
    transform: translateY(100%);
  }

  .bottom-sheet__content-wrapper {
    transition: 300ms cubic-bezier(0, 0, 0.2, 1);
  }

  .bottom-sheet__content {
    background-color: var(
      --free-ui-bottom-sheet-bg-color,
      rgb(var(--background-neutral-primary))
    );
    border-top-left-radius: var(
      --free-ui-bottom-sheet-border-top-radius,
      1.5rem
    );
    border-top-right-radius: var(
      --free-ui-bottom-sheet-border-top-radius,
      1.5rem
    );
    transition: cubic-bezier(0, 0, 0.2, 1);
    min-height: var(--free-ui-bottom-sheet-min-height, 0px);
    max-height: calc(
      100vh - var(--bottom-sheet-top-spacing, 60px) - var(--status-bar-height)
    );
  }

  .bottom-sheet__content--full {
    height: calc(
      100vh - var(--bottom-sheet-top-spacing, 60px) - var(--status-bar-height)
    );
  }

  /* Full-screen: covers the whole viewport, no top gap or top rounding. */
  .bottom-sheet__content--fullscreen {
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
</style>
